import {
  faPlus as AddIcon,
  faXmark as XIcon
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MDXEditor, type MDXEditorMethods } from '@mdxeditor/editor';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField
} from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import { sortBy } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useWatch
} from 'react-hook-form';

import { TEAM_QUEUE } from '@/features/TicketCreateGeneralInformation/TicketCreateGeneralInformation.constants';
import useStaticData from '@/hooks/StaticData.hooks';
import {
  type TemplateDialogPropsType,
  type TicketTemplateFormType,
  type TicketTemplateType,
  useAdministrationTemplates
} from '@/pages/Administration/AdministrationTemplates';
import { useFetchOwnersList } from '@/pages/Ticket';
import { mdxFormatDescriptionSummary } from '@/pages/Ticket/Ticket.utils';
import { MDX_EDITOR_CONFIG } from '@/pages/Ticket/TicketCreate/TicketCreate.constants';
import userAtom from '@/store/user.store';
import { sanitizeMarkdownContent } from '@/utils/Common.utils';

import { DEFAULT_TEMPLATE_DIALOG_VALUES } from './TicketTemplateDialog.constants';
import useStyles from './TicketTemplateDialog.styles';

const TicketTemplateDialog = (props: TemplateDialogPropsType) => {
  const { open, setOpen, templateType, selectedTemplate } = props;

  const [user] = useAtom(userAtom);
  const {
    updateTemplate,
    createTemplate,
    createError,
    createSuccess,
    updateError,
    updateSuccess
  } = useAdministrationTemplates();
  const { mode: themeMode } = useColorScheme();
  const { data: owners } = useFetchOwnersList();
  const ref = useRef<MDXEditorMethods>(null);
  const { categories, priorities } = useStaticData();

  const form = useForm({
    defaultValues: DEFAULT_TEMPLATE_DIALOG_VALUES as TicketTemplateFormType
  });
  const {
    clearErrors,
    control,
    formState: { errors },
    setValue,
    getValues,
    handleSubmit
  } = form;
  const { append, fields, remove } = useFieldArray({
    control,
    name: 'recommendations'
  });

  const { classes } = useStyles({ errors });

  const categoryId = useWatch({ control, name: 'category_id' });
  const ownerId = useWatch({ control, name: 'owner_id' });
  const priorityId = useWatch({ control, name: 'priority_id' });

  //-----------------------------------------------------------------
  const categoriesList = useMemo(() => {
    const sortedCategories = sortBy(categories, 'name').filter(
      (category) => category.ticket_type === 1
    );
    return sortedCategories;
  }, [categories]);

  const ownersList = useMemo(() => {
    const sortedOwners = sortBy(owners, 'name');
    return sortedOwners;
  }, [owners]);

  const selectedCategory = useMemo(() => {
    return categories.find((c) => c.id === categoryId) || null;
  }, [categories, categoryId]);

  const selectedOwner = useMemo(() => {
    return owners.find((o) => o.id === ownerId) || null;
  }, [owners, ownerId]);

  const selectedPriority = useMemo(() => {
    return priorities.find((p) => p.id === priorityId) || null;
  }, [priorities, priorityId]);

  useEffect(() => {
    // if editing, set form field state with template values
    if (selectedTemplate && templateType.includes('Edit')) {
      setValue('category_id', selectedTemplate?.category_id);
      setValue('owner_id', selectedTemplate?.owner_id);
      setValue('priority_id', selectedTemplate?.priority_id);
      setValue(
        'recommendations',
        JSON.parse(selectedTemplate?.recommendations || '[]')
      );
      setValue('summary', selectedTemplate?.summary);
      setValue('team_queue', selectedTemplate?.team_queue);
      setValue('template_name', selectedTemplate?.template_name ?? '');
      setValue('title', selectedTemplate?.title ?? '');
    }
  }, [selectedTemplate, open]);

  useEffect(() => {
    if (createSuccess) {
      setOpen(false);
    }
    if (updateSuccess) {
      setOpen(false);
    }
  }, [createSuccess, updateSuccess]);
  //-----------------------------------------------------------------

  const handleChange = (value: string) => {
    clearErrors('summary');
    setValue('summary', value, { shouldDirty: true });
  };

  const handleSave = (actionId?: string) => {
    const newTemplateInfo: {
      [key: string]: string | TicketTemplateType | number;
    } = {};
    const templateData = {
      category_id: getValues('category_id'),
      generic: '',
      owner_id: getValues('owner_id'),
      priority_id: getValues('priority_id'),
      recommendations: JSON.stringify(getValues('recommendations')),
      summary: sanitizeMarkdownContent(getValues('summary') as string),
      team_queue: getValues('team_queue'),
      template_name: getValues('template_name'),
      title: getValues('title'),
      user_id: user?.id
    };
    newTemplateInfo.templateType = 'ticket';
    newTemplateInfo.data = templateData as TicketTemplateType;

    if (actionId === 'save') {
      newTemplateInfo.id = selectedTemplate.id as number;
      updateTemplate(
        newTemplateInfo as {
          templateType: string;
          id: number;
          data: TicketTemplateType;
        }
      );
    } else {
      createTemplate(
        newTemplateInfo as { templateType: string; data: TicketTemplateType }
      );
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      scroll="paper"
      onClose={() => {
        setOpen(false);
      }}
    >
      <DialogTitle>{`${templateType} Template`}</DialogTitle>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(handleSave as () => void)} noValidate>
          <DialogContent>
            <Stack spacing={2}>
              {createError && typeof createError === 'string' && (
                <Alert severity="error">{createError}</Alert>
              )}
              {updateError && typeof updateError === 'string' && (
                <Alert severity="error">{updateError}</Alert>
              )}
              <div>
                <InputLabel htmlFor="template-name-input">
                  Template Name *
                </InputLabel>
                <FormControl id="template-name-input" fullWidth>
                  <Controller
                    control={control}
                    name="template_name"
                    rules={{
                      minLength: 5,
                      required: true
                    }}
                    render={({ field, formState }) => (
                      <TextField
                        id="template-name"
                        error={!!formState.errors.template_name}
                        {...field}
                      />
                    )}
                  />
                </FormControl>
              </div>
              <Divider />
              <div>
                <InputLabel htmlFor="title-input">Title *</InputLabel>
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="title"
                    rules={{
                      required: true
                    }}
                    render={({ field, formState }) => (
                      <TextField
                        id="template-title"
                        error={!!formState.errors.title}
                        {...field}
                      />
                    )}
                  />
                </FormControl>
              </div>
              <div>
                <InputLabel htmlFor="summary-editor">Summary *</InputLabel>
                <FormControl id="summary-editor" fullWidth>
                  <Controller
                    control={control}
                    name="summary"
                    rules={{
                      required: true
                    }}
                    render={() => (
                      <div className={classes.summaryErrorBorder}>
                        <MDXEditor
                          className={`${themeMode}-theme dark-editor`}
                          contentEditableClassName="cm-richView"
                          markdown={mdxFormatDescriptionSummary(
                            selectedTemplate.summary as string
                          )}
                          plugins={MDX_EDITOR_CONFIG}
                          ref={ref}
                          suppressHtmlProcessing
                          onChange={(value) => {
                            handleChange(value);
                          }}
                        />
                      </div>
                    )}
                  />
                </FormControl>
              </div>
              <Stack>
                <InputLabel htmlFor="priority-autocomplete">
                  Priority
                </InputLabel>
                <FormControl id="priority-autocomplete" fullWidth>
                  <Controller
                    control={control}
                    name="priority_id"
                    render={() => (
                      <Autocomplete
                        options={priorities}
                        getOptionLabel={(option) => option.name}
                        onChange={(_e, value) =>
                          value
                            ? setValue('priority_id', value.id)
                            : setValue('priority_id', null)
                        }
                        value={selectedPriority}
                        renderInput={(params) => <TextField {...params} />}
                      />
                    )}
                  />
                </FormControl>
              </Stack>
              <Divider />
              <Stack sx={{ alignItems: 'flex-start' }} spacing={1}>
                <div className={classes.formField}>
                  <InputLabel>Recommendations</InputLabel>
                  <Stack spacing={2}>
                    {fields.map((recommendation, index) => (
                      <Controller
                        key={recommendation.id}
                        render={({ field }) => (
                          <FormControl
                            id={`recommendation-inputs-${recommendation.id}`}
                            fullWidth
                          >
                            <OutlinedInput
                              {...field}
                              endAdornment={
                                index > 0 && (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={() => remove(index)}
                                      size="small"
                                    >
                                      <FontAwesomeIcon size="sm" icon={XIcon} />
                                    </IconButton>
                                  </InputAdornment>
                                )
                              }
                            />
                          </FormControl>
                        )}
                        name={`recommendations.${index}.body`}
                        control={control}
                      />
                    ))}
                  </Stack>
                </div>
                <IconButton
                  sx={{ bgcolor: 'primary.main', color: 'white' }}
                  onClick={() => {
                    append({
                      id: getValues('recommendations').length + 1,
                      body: ''
                    });
                  }}
                  size="small"
                >
                  <FontAwesomeIcon icon={AddIcon} />
                </IconButton>
              </Stack>
              <Divider />
              <Stack
                sx={{ flexBasis: 1 }}
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
              >
                <div className={classes.formField}>
                  <InputLabel htmlFor="category-autocomplete">
                    Categories
                  </InputLabel>
                  <FormControl id="category-autocomplete" fullWidth>
                    <Controller
                      control={control}
                      name="category_id"
                      render={() => (
                        <Autocomplete
                          fullWidth
                          options={categoriesList}
                          getOptionLabel={(option) => option.name}
                          onChange={(_e, value) =>
                            value
                              ? setValue('category_id', value.id)
                              : setValue('category_id', null)
                          }
                          value={selectedCategory}
                          renderInput={(params) => <TextField {...params} />}
                        />
                      )}
                    />
                  </FormControl>
                </div>
                <div className={classes.formField}>
                  <InputLabel htmlFor="team-queue-select">
                    Team Queue
                  </InputLabel>
                  <FormControl id="team-queue-select" fullWidth>
                    <Controller
                      control={control}
                      name="team_queue"
                      render={({ field }) => (
                        <Select id="team-queue-select" {...field}>
                          {TEAM_QUEUE.map((item) => (
                            <MenuItem key={item} value={item}>
                              {item}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </FormControl>
                </div>
                <div className={classes.formField}>
                  <InputLabel htmlFor="owner-autocomplete">Owner</InputLabel>
                  <FormControl id="owner-autocomplete" fullWidth>
                    <Controller
                      control={control}
                      name="owner_id"
                      render={() => (
                        <Autocomplete
                          getOptionLabel={(option) => option.name}
                          onChange={(_e, value) =>
                            value
                              ? setValue('owner_id', value.id)
                              : setValue('owner_id', null)
                          }
                          options={ownersList}
                          renderInput={(params) => <TextField {...params} />}
                          renderOption={(ownerProps, option) => (
                            <Box
                              component="li"
                              {...ownerProps}
                              sx={{ typography: 'body1' }}
                            >
                              {option.name}
                            </Box>
                          )}
                          value={selectedOwner}
                        />
                      )}
                    />
                  </FormControl>
                </div>
              </Stack>
              <Divider />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              {templateType.includes('New') && (
                <Button type="submit" variant="contained">
                  Create Template
                </Button>
              )}
              {templateType.includes('Edit') && (
                <Button onClick={() => handleSave('save')} variant="contained">
                  Save Changes
                </Button>
              )}
            </Stack>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default TicketTemplateDialog;
